import { t } from "@travily/i18n";
import { ActionMenu } from "@ui/components/ActionMenu";
import { OutlineButton, type OutlineButtonProps } from "@ui/components/OutlineButton";
import { searchBarPillButton } from "@ui/components/SearchBar/searchBarPillButton";
import { Button } from "@ui/primitives/Button";
import { composeRenderProps } from "@ui/primitives/shared";
import { SwitchVertical01 } from "@untitledui/icons";

export interface SortOption {
  label: string;
  value: string;
}

export interface SortMenuProps {
  currentValue: string;
  onChange: (value: string) => void;
  options: SortOption[];
  size?: OutlineButtonProps["size"];
  /** Trigger presentation: a standalone OutlineButton (default), the pill
   *  matching `SearchBar.Filters` for the SearchBar trailing row, or a
   *  compact icon-only circle for tight strips (e.g. the map sheet header). */
  variant?: "outline" | "searchbar-pill" | "icon";
}

export function SortMenu({ currentValue, onChange, options, size = "40", variant = "outline" }: SortMenuProps) {
  const sortLabel = t("components.sort_menu.sort_label");
  const currentLabel = options.find((opt) => opt.value === currentValue)?.label;

  return (
    <ActionMenu
      items={options.map((option) => ({ id: option.value, label: option.label }))}
      label={sortLabel}
      onSelectionChange={onChange}
      placement="bottom end"
      selectedKey={currentValue}
      selectionMode="single"
      title={sortLabel}
      trigger={
        variant === "searchbar-pill" ? (
          <SearchBarPillTrigger currentLabel={currentLabel ?? sortLabel} label={sortLabel} />
        ) : variant === "icon" ? (
          <OutlineButton aria-label={sortLabel} leadIcon={<SwitchVertical01 />} shape="circle" size={size} />
        ) : (
          <OutlineButton leadIcon={<SwitchVertical01 />} size={size}>
            {currentLabel ?? sortLabel}
          </OutlineButton>
        )
      }
    />
  );
}

function SearchBarPillTrigger({ currentLabel, label }: { currentLabel: string; label: string }) {
  const styles = searchBarPillButton();

  return (
    <Button
      aria-label={label}
      className={composeRenderProps("ml-4", (className, renderProps) => styles.base({ ...renderProps, className }))}
    >
      <span aria-hidden="true" className={styles.icon()}>
        <SwitchVertical01 />
      </span>
      <span className={styles.label()}>{currentLabel}</span>
    </Button>
  );
}
